"use client";

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import type { Dog } from '@/lib/types';
import Image from 'next/image';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
    type CarouselApi
} from '@/components/ui/carousel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { InquiryForm } from './InquiryForm';
import { ShieldCheck, ArrowRight, ChevronLeft, Activity, Calendar, Award } from 'lucide-react';
import { m, AnimatePresence } from 'framer-motion';
import { useAnalytics } from '@/hooks/use-analytics';
import { cn } from '@/lib/utils';
import { differenceInWeeks, parseISO } from 'date-fns';

interface DogDetailViewProps {
    dog: Dog;
    startInInquireMode?: boolean;
    onInquiryComplete?: () => void;
}

export function DogDetailView({ dog, startInInquireMode = false, onInquiryComplete }: DogDetailViewProps) {
    const [showInquiryForm, setShowInquiryForm] = useState(startInInquireMode);
    const [api, setApi] = useState<CarouselApi>();
    const [currentIndex, setCurrentIndex] = useState(0);
    const { logEvent } = useAnalytics();

    // Track Views
    useEffect(() => {
        logEvent(dog.id, 'view');
    }, [dog.id, logEvent]);

    const sortedImages = useMemo(() => {
        if (!dog.images || dog.images.length === 0) return [];
        if (!dog.primaryImage) return dog.images;
        const otherImages = dog.images.filter(img => img !== dog.primaryImage);
        return [dog.primaryImage, ...otherImages];
    }, [dog.images, dog.primaryImage]);

    useEffect(() => {
        setShowInquiryForm(startInInquireMode);
    }, [startInInquireMode]);

    const calculateAge = (dobString?: string) => {
        if (!dobString) return 'Puppy';
        try {
            const weeks = differenceInWeeks(new Date(), parseISO(dobString));
            if (weeks < 1) return 'Newborn';
            if (weeks < 20) return `${weeks} weeks`;
            return `${Math.floor(weeks / 4)} months`;
        } catch (e) {
            return 'Puppy';
        }
    };

    const displayAttributes = [
        { icon: Award, label: 'Breed', value: dog.breed, color: 'text-primary' },
        { icon: Activity, label: 'Gender', value: dog.gender, color: 'text-blue-400' },
        { icon: Calendar, label: 'Age', value: calculateAge(dog.dateOfBirth), color: 'text-emerald-400' },
        { icon: ShieldCheck, label: 'Color', value: dog.color, color: 'text-amber-400' },
    ];

    const scrollToImage = useCallback((index: number) => {
        logEvent(dog.id, 'image_click', { index });
        api?.scrollTo(index);
    }, [api, dog.id, logEvent]);

    // Track carousel index
    useEffect(() => {
        if (!api) return;

        api.on("select", () => {
            const index = api.selectedScrollSnap();
            setCurrentIndex(index);
            logEvent(dog.id, 'carousel_swipe', { index });
        });
    }, [api, dog.id, logEvent]);

    return (
        <div className="w-full bg-background min-h-[600px] text-foreground">
            <AnimatePresence mode="wait">
                {!showInquiryForm ? (
                    <m.div
                        key="info"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex flex-col lg:flex-row w-full h-full"
                    >
                        {/* Elegant Gallery Side */}
                        <div className="w-full lg:w-1/2 p-8 lg:p-12 flex flex-col gap-6 bg-card border-r border-border">
                            {/* Main Stage - Shadcn Carousel */}
                            <div className="relative w-full aspect-[4/5] rounded-[2rem] overflow-hidden bg-secondary/30 shadow-soft group border border-border">
                                <Carousel setApi={setApi} opts={{ loop: true }} className="w-full h-full">
                                    <CarouselContent className="h-full ml-0">
                                        {sortedImages.map((img, idx) => (
                                            <CarouselItem key={idx} className="pl-0 h-full relative">
                                                <Image
                                                    src={img}
                                                    alt={`${dog.name} view ${idx + 1}`}
                                                    fill
                                                    className="object-cover"
                                                    priority={idx === 0}
                                                />
                                            </CarouselItem>
                                        ))}
                                    </CarouselContent>
                                    {sortedImages.length > 1 && (
                                        <>
                                            <CarouselPrevious className="left-4 bg-background/80 hover:bg-background border-border" />
                                            <CarouselNext className="right-4 bg-background/80 hover:bg-background border-border" />
                                        </>
                                    )}
                                </Carousel>
                            </div>

                            {/* Thumbnail Strip */}
                            {sortedImages.length > 1 && (
                                <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide snap-x">
                                    {sortedImages.map((img, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => scrollToImage(idx)}
                                            className={cn(
                                                "relative w-20 h-20 rounded-xl overflow-hidden shrink-0 transition-all duration-200 snap-start border-2",
                                                currentIndex === idx
                                                    ? "border-primary opacity-100 shadow-sm"
                                                    : "border-border opacity-50 hover:opacity-80 hover:border-primary/50"
                                            )}
                                        >
                                            <Image
                                                src={img}
                                                fill
                                                alt={`Thumbnail ${idx}`}
                                                className="object-cover"
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Details Side */}
                        <div className="w-full lg:w-1/2 p-8 lg:p-12 flex flex-col h-full overflow-y-auto bg-background">
                            <div className="space-y-8 max-w-xl">
                                {/* Header */}
                                <div>
                                    <div className="flex items-center justify-between mb-5">
                                        <Badge variant="outline" className="rounded-full px-4 py-1.5 bg-card border-border text-foreground uppercase tracking-[0.15em] text-[10px] font-bold shadow-sm">
                                            {dog.status}
                                        </Badge>
                                        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
                                            ID: #{dog.id.slice(-6)}
                                        </div>
                                    </div>
                                    <h1 className="font-headline text-5xl font-medium text-foreground tracking-tight mb-2 leading-[1.1]">
                                        {dog.name}
                                    </h1>
                                    <p className="text-muted-foreground text-lg font-medium">{dog.breed}</p>
                                </div>

                                {/* Price & CTA */}
                                <div className="bg-card rounded-2xl p-6 shadow-soft border border-border space-y-5">
                                    <div className="flex items-baseline justify-between pb-5 border-b border-border">
                                        <span className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">Adoption Fee</span>
                                        <span className="text-4xl font-headline font-semibold text-foreground tracking-tight">
                                            {dog.price ? `$${dog.price.toLocaleString()}` : 'Inquire'}
                                        </span>
                                    </div>

                                    <Button
                                        onClick={() => {
                                            logEvent(dog.id, 'inquiry_click');
                                            setShowInquiryForm(true);
                                        }}
                                        className="w-full h-14 rounded-full text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
                                    >
                                        Inquire About {dog.name}
                                        <ArrowRight className="w-4 h-4" />
                                    </Button>

                                    <div className="flex items-center justify-center gap-2 text-muted-foreground text-[10px] font-bold uppercase tracking-[0.12em] pt-1">
                                        <ShieldCheck className="w-3.5 h-3.5" />
                                        <span>Health Guarantee</span>
                                    </div>
                                </div>

                                {/* Attributes Grid */}
                                <div className="grid grid-cols-2 gap-3">
                                    {displayAttributes.map((attr) => (
                                        <div key={attr.label} className="flex flex-col p-5 rounded-2xl bg-card border border-border shadow-sm">
                                            <div className="flex items-center gap-2 mb-2">
                                                <attr.icon className={cn("w-3.5 h-3.5", attr.color)} />
                                                <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">{attr.label}</p>
                                            </div>
                                            <p className="text-foreground font-semibold text-base leading-tight">{attr.value}</p>
                                        </div>
                                    ))}
                                </div>

                                {/* Description */}
                                <div className="bg-card rounded-2xl p-6 shadow-soft border border-border">
                                    <h3 className="font-headline text-xl text-foreground mb-3">About {dog.name}</h3>
                                    <div className="text-muted-foreground leading-relaxed text-sm">
                                        <p>{dog.description || `Meet ${dog.name}, a wonderful ${dog.breed} looking for a loving home.`}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </m.div>
                ) : (
                    <m.div
                        key="inquiry"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="flex flex-col h-full bg-background p-6 lg:p-12"
                    >
                        <div className="max-w-3xl mx-auto w-full h-full flex flex-col">
                            <Button
                                variant="ghost"
                                onClick={() => setShowInquiryForm(false)}
                                className="self-start text-muted-foreground hover:text-foreground hover:bg-card -ml-4 mb-8 rounded-full px-6"
                            >
                                <ChevronLeft className="w-5 h-5 mr-2" />
                                Return to {dog.name}
                            </Button>

                            <div className="mb-10 text-center">
                                <h2 className="font-headline text-4xl text-foreground mb-4">Adoption Inquiry</h2>
                                <p className="text-muted-foreground text-lg font-light">Complete this form to connect about <span className="font-bold text-foreground">{dog.name}</span>.</p>
                            </div>

                            <div className="flex-1 bg-card rounded-[2.5rem] border border-border shadow-xl p-10 overflow-y-auto">
                                <InquiryForm
                                    dog={dog}
                                    onFormSubmit={onInquiryComplete || (() => { })}
                                    onCancel={() => setShowInquiryForm(false)}
                                />
                            </div>
                        </div>
                    </m.div>
                )}
            </AnimatePresence>
        </div>
    );
}
